// Copyright © 2024 Broadcom Inc. and its subsidiaries. All Rights Reserved.
import React, { useEffect, useState } from 'react';
import compose from 'recompose/compose';
import { connect } from 'react-redux';
import { useIntl } from 'react-intl';
import {
  withStyles,
  Typography,
  Grid,
  FormHelperText,
} from '@material-ui/core';
import copy from 'copy-to-clipboard';
import { some, get, includes, isArray } from 'lodash';
import queryString from 'query-string';
import { arrayOf, shape, string, func, bool } from 'prop-types';

import { getConfig } from '../../../../../reducers/portalConfig';
import { getI18n, getI18nFormattedMessage } from '../../../../../utils/intl';
import {
  AlertMessages,
  ErrorContainer,
  FormTextField,
  FormActionButtons,
  FormSelect,
  FormDefaultTextField,
  FileDropzone,
} from '../../../../../components';
import {
  setAuthSettings,
  updateAuthScheme,
  saveAuthScheme,
} from '../../../../../actions/authenticationSettings';
import {
  hasError,
  urlStringSlice,
  getFileExtension,
  getSAMLDefaultSPId,
  createGUID,
  getDefaultSAMLACSURL,
  validateDomain,
} from '../../../../../utils';
import { SAML_BINDINGS, SAML_TOKEN_ATTRIBUTES } from '../../label';
import styles from '../../styles';
import Actions from '../../Actions';
import { ALERT_SUCCESS } from '../../../../../constants';

export const SamlProviderConfiguration = (props) => {
  const {
    classes,
    errors = [],
    authDetails,
    uuid,
    tabValue,
    onCancelDetails,
    notifyMessages,
    hasAttributeMappingFields,
    hasDetailsFields,
    isOktaIdpFlag,
    config,
  } = props;

  const intl = getI18n(useIntl());
  const queryParam = queryString.parse(get(props, 'location.search'));
  const qParam = get(queryParam, 'authType') ? `?authType=${queryParam.authType}` : '';
  const tenantId = get(config, 'portal.tenantPrefix');
  const generateAcsUrl = getDefaultSAMLACSURL(createGUID(), tenantId);
  const acsUrlArray = generateAcsUrl ? urlStringSlice(generateAcsUrl, '/api/') : [];
  const generateAcsUrl2 = acsUrlArray.length > 0 ? acsUrlArray[0] : '';
  const generateAcsUrl3 = acsUrlArray.length > 1 ? acsUrlArray[1] : '';

  const [notificationMessage, setNotificationMessage] = useState('');
  const [notificationStatus, setNotificationStatus] = useState('');
  const [acsUrl1, setAcsUrl1] = useState('https://');
  const [acsUrl2, setAcsUrl2] = useState(generateAcsUrl2);
  const [acsUrl3, setAcsUrl3] = useState(generateAcsUrl3);
  const [isAcsUrl, setIsAcsUrl] = useState(false);
  const [providerUrl, setProviderUrl] = useState('');
  const [isProviderUrl, setIsProviderUrl] = useState(false);
  const [issueId, setIssueId] = useState('');
  const [isIssueId, setIsIssueId] = useState(false);
  const [samlBinding, setSamlBinding] = useState('post');
  const [isSamlBinding, setIsSamlBinding] = useState(false);
  const [tokenAttribute, setTokenAttribute] = useState('SAMLResponse');
  const [isTokenAttribute, setIsTokenAttribute] = useState(false);
  const [tokenAttributeIn, setTokenAttributeIn] = useState('parameter');
  const [isTokenAttributeIn, setIsTokenAttributeIn] = useState(false);
  const [providerId, setProviderId] = useState(getSAMLDefaultSPId());
  const [isProviderId, setIsProviderId] = useState(false);
  const [isUnsavedChanges, setIsUnsavedChanges] = useState(false);
  const [isSaveDisabled, setIsSaveDisabled] = useState(true);
  const [isFileValidation, setIsFileValidation] = useState(false);
  const [fileValidationMessage, setFileValidationMessage] = useState('');
  const [emailDomain, setEmailDomain] = useState('');
  const [isEmailDomain, setIsEmailDomain] = useState(false);

  const FILE_TYPE = '.crt, .cer, .ca-bundle, .p7b, .p7c, .p7s, .pem, .cert';
  const FILE_HELPER_TEXT = intl.getI18nMessage('label.upload.file.help.text', {
    ext: '.crt, .cer, .ca-bundle, .p7b, .p7c, .p7s, .pem, .cert', type : 'certificate', size: '50kb' });
  const MAX_FILE_SIZE = 50*1024;
  const ALLOWED_FILE_EXTENSIONS = ['crt', 'cer', 'ca-bundle', 'p7b', 'p7c', 'p7s', 'pem', 'cert'];

  const [sslFile, setSslFile] = useState({
    files: [],
    acceptFileTypes: FILE_TYPE,
    maxFiles: 1,
    error: false,
    helperText: FILE_HELPER_TEXT,
    certificate: '',
    certificateFileName: '',
  });

  useEffect(() => {
    if(tabValue === 'provider-configuration-tab') {
      if(localStorage.getItem('isUnSavedChanges') === 'true') {
        setIsUnsavedChanges(true);
      } else {
        setIsUnsavedChanges(false);
      }
      const acsUrlArray = get(authDetails, 'ConfigurationParams.acsUrl') ?
      urlStringSlice(get(authDetails, 'ConfigurationParams.acsUrl'), '/api/') : [];
      setAcsUrl1('https://');
      if(acsUrlArray.length > 0) setAcsUrl2(acsUrlArray[0]);
      if(acsUrlArray.length > 1) setAcsUrl3(acsUrlArray[1]);
      setProviderUrl(get(authDetails, 'ConfigurationParams.url'));
      setIssueId(get(authDetails, 'ConfigurationParams.issuerId'));
      setEmailDomain(get(authDetails,'ConfigurationParams.emailDomain'));
      if(get(authDetails, 'ConfigurationParams.samlBinding'))
        setSamlBinding(get(authDetails, 'ConfigurationParams.samlBinding'));
      if(get(authDetails, 'ConfigurationParams.samlTokenAttribute'))
        setTokenAttribute(get(authDetails, 'ConfigurationParams.samlTokenAttribute'));
      else
      setTokenAttribute('SAMLResponse');
      if(get(authDetails, 'ConfigurationParams.samlTokenAttributeIn'))
        setTokenAttributeIn(get(authDetails, 'ConfigurationParams.samlTokenAttributeIn'));
      if(get(authDetails, 'ConfigurationParams.serviceProviderId'))
        setProviderId(get(authDetails, 'ConfigurationParams.serviceProviderId'));
      const certName = get(authDetails, 'ConfigurationParams.certificateFileName');
        if(certName) {
          sslFile.files = [];
          const file1 = new File([
            new Blob([]),
          ], get(authDetails, 'ConfigurationParams.certificateFileName'));
          sslFile.files.push(file1);
          setSslFile({ ...sslFile, ...{
            certificateFileName: certName,
          } });
      }
    }
  }, [authDetails, tabValue]);

  useEffect(() => {
    if(isUnsavedChanges) {
      const hasSave = !hasAttributeMappingFields(authDetails, queryParam) ||
      !hasDetailsFields(authDetails) || hasError(acsUrl2, true)
         || hasError(providerUrl, true) || hasError(samlBinding, true) ||
         hasError(tokenAttribute, true) ||hasError(tokenAttributeIn, true);
      setIsSaveDisabled(hasSave);
    }
  }, [isUnsavedChanges, acsUrl2, providerUrl, samlBinding,
    tokenAttribute, tokenAttributeIn, tabValue]);

  const fileLoad = (file) => {
    const reader = new FileReader();
    reader.onabort = () => {
      sslFile.error = true;
      setSslFile({ ...sslFile });
    };
    reader.onerror = () => {
      sslFile.error = true;
      setSslFile({ ...sslFile });
    };
    reader.onload = (e) => {
      if (hasValidateFile(file)) {
        notifyMessages('', '');
        sslFile.error = false;
        const fileContent = reader.result;
        const fileExtenstion = getFileExtension(file.name, ALLOWED_FILE_EXTENSIONS);
        const fileData = file;
        fileData.ext = fileExtenstion;
        fileData.blob = new Blob(
          [fileContent],
          { type: FILE_TYPE },
        );
        sslFile.certificate = e.target.result;
        sslFile.certificateFileName = file.name;
        sslFile.files.push(fileData);
        setSslFile({ ...sslFile });
      } else {
        setSslFile({ ...sslFile });
      }
    };
    reader.readAsDataURL(file);
  };

  const handleFiles = (files) => {
    if (isArray(files)) {
      files.forEach((file) => {
        fileLoad(file);
      });
    } else {
      fileLoad(files);
    }
    localStorage.setItem('isUnSavedChanges', true);
    setIsUnsavedChanges(true);
  };

  const onRemoveFile = (file) => {
    clearErrorMessages();
    const remainFiles = sslFile.files.filter((obj) => obj.name !== file.name);
    setSslFile({ ...sslFile, files: remainFiles });
    localStorage.setItem('isUnSavedChanges', true);
    setIsUnsavedChanges(true);
  };

  const clearErrorMessages = () => {
    setSslFile({ ...sslFile, error: false });
    setIsFileValidation(false);
    notifyMessages('', '');
  }

  const hasValidSaveButton = (selectedFiles) => {
    if (selectedFiles.files.length !== 1) {
      return true;
    }
  }

  const hasValidateFile = (file) => {
    const extension = getFileExtension(file.name, ALLOWED_FILE_EXTENSIONS);
    if (!includes(ALLOWED_FILE_EXTENSIONS, extension)) {
      sslFile.error = true;
      return false;
    }

    if (file.size > MAX_FILE_SIZE) {
      sslFile.error = true;
      setIsFileValidation(true);
      setFileValidationMessage(getI18nFormattedMessage('label.error.upload.file.size'));
      return false;
    }
    if (some(sslFile.files, { name: file.name })) {
      setIsFileValidation(true);
      setFileValidationMessage(getI18nFormattedMessage('error.gateway.bundle.file.name.unique'));
      return false;
    }
    if (sslFile.files.length === 1) {
      setIsFileValidation(true);
      setFileValidationMessage(getI18nFormattedMessage('label.error.auth.file.count'));
      return false;
    }
    setIsFileValidation(false);
    return true;
  };

  const handleChange = (fieldName, value) => {
    switch (fieldName) {
      case 'acsUrl': {
        setAcsUrl2(value);
        setIsAcsUrl(hasError(value, true));
        break;
      }
      case 'providerUrl': {
        setProviderUrl(value);
        setIsProviderUrl(hasError(value, true));
        break;
      }
      case 'issueId': {
        setIssueId(value);
        setIsIssueId(hasError(value, true));
        break;
      }
      case 'samlBinding': {
        setSamlBinding(value);
        setIsSamlBinding(hasError(value, true));
        break;
      }
      case 'tokenAttribute': {
        setTokenAttribute(value);
        setIsTokenAttribute(hasError(value, true));
        break;
      }
      case 'tokenAttributeIn': {
        setTokenAttributeIn(value);
        setIsTokenAttributeIn(hasError(value, true));
        break;
      }
      case 'providerId': {
        setProviderId(value);
        setIsProviderId(hasError(value, true));
        break;
      }
      case 'emailDomain': {
        setEmailDomain(value);
        const emailArray = value.split(',');
        if(value) {
          if(emailArray.length > 0 ) {
            emailArray.forEach(element => {
              setIsEmailDomain(!validateDomain(element));
            });
          }
        }
        break;
      }
    }
    localStorage.setItem('isUnSavedChanges', true);
    setIsUnsavedChanges(true);
  }


  const onNext = () => {
    const acsUrlError = hasError(acsUrl2, true);
    const issueIdError = hasError(issueId, true);
    const providerUrlError = hasError(providerUrl, true);
    const samlBindingError = hasError(samlBinding, true);
    const tokenAttributeError = hasError(tokenAttribute, true);
    const tokenAttributeInError = hasError(tokenAttributeIn, true);
    const providerIdError = hasError(providerId, true);
    let emailDomainError = isEmailDomain;
    if(isOktaIdpFlag) {
      const emailArray = emailDomain ? emailDomain.split(',') : [];
        if(emailDomain) {
          if(emailArray.length > 0 ) {
            emailArray.forEach(element => {
              emailDomainError = !validateDomain(element);
            });
          }
        }
    }

    if (some([
      acsUrlError, providerUrlError, samlBindingError, tokenAttributeError,
      tokenAttributeInError, issueIdError, providerIdError, hasValidSaveButton(sslFile),
      emailDomainError,
    ])) {
      setIsAcsUrl(acsUrlError);
      setIsProviderUrl(providerUrlError);
      setIsSamlBinding(samlBindingError);
      setIsTokenAttribute(tokenAttributeError);
      setIsTokenAttributeIn(tokenAttributeInError);
      setIsIssueId(issueIdError);
      setIsProviderId(providerIdError)
      setIsEmailDomain(emailDomainError);
      if (hasValidSaveButton(sslFile)) {
        setSslFile({ ...sslFile, error: true });
        setNotificationStatus('error');
        setNotificationMessage(getI18nFormattedMessage('label.error.auth.file.upload.text'));
      }
    } else {
      setNotificationStatus('');
      setNotificationMessage('');
      const aUrl = acsUrl1+acsUrl2+acsUrl3;
      const gUidArray = aUrl.split('/response/');
      const generateUuid = uuid ? uuid : gUidArray.length > 1 ? gUidArray[1] : '' ;
      let configParams = {
        acsUrl: aUrl,
        issuerId: issueId,
        samlBinding: samlBinding,
        samlTokenAttribute: tokenAttribute,
        samlTokenAttributeIn: tokenAttributeIn,
        serviceProviderId: providerId,
        url: providerUrl,
        emailDomain: emailDomain,
        certificateFileName: get(sslFile, 'certificateFileName'),
      };
      configParams = uuid ? { ...authDetails.ConfigurationParams, ...configParams } : configParams;
      configParams = get(sslFile, 'certificate') ? { ...configParams, ...{ certificate: get(sslFile, 'certificate') } } :
      configParams;
      const authType = uuid ? authDetails.authType : 'PORTAL';
      const data = { ...authDetails, ...{
        ConfigurationParams: configParams,
        authMethod: 'SAML',
        uuid: generateUuid,
        authType: authType,
        advancedConfigurations: {},
        } };
        props.setAuthSettings(data);
        const url = uuid ? `/publish/settings/authentication/edit/${uuid}/attribute-mapping${qParam}`
         : `/publish/settings/authentication/add/attribute-mapping${qParam}`;
        props.push(url);
    }
  }

  const onSave = () => {
    const aUrl = acsUrl1+acsUrl2+acsUrl3;
    const gUidArray = aUrl.split('/response/');
    const generateUuid = uuid ? uuid : gUidArray.length > 1 ? gUidArray[1] : '' ;
    let configParams = {
      acsUrl: aUrl,
      issuerId: issueId,
      samlBinding: samlBinding,
      samlTokenAttribute: tokenAttribute,
      samlTokenAttributeIn: tokenAttributeIn,
      serviceProviderId: providerId,
      url: providerUrl,
      emailDomain: emailDomain,
      certificateFileName: get(sslFile, 'certificateFileName'),
    };
    configParams = get(sslFile, 'certificate') ? { ...configParams, ...{ certificate: get(sslFile, 'certificate') } } :
    configParams;

    const data = { ...authDetails, ...{
      ConfigurationParams: configParams,
      advancedConfigurations: {},
      authMethod: 'SAML',
      uuid: generateUuid,
      } };
    if (uuid) {
      props.updateAuthScheme(uuid, data);
    } else {
      props.saveAuthScheme(uuid, data);
    }
  }

  const onCancel = () => {
    onCancelDetails(isUnsavedChanges);
  }

  const onCopyClick = () => {
   const url = acsUrl1+acsUrl2+acsUrl3;
    copy(url);
    notifyMessages(getI18nFormattedMessage('label.copy.success.text', { 'name': 'url' }),
    ALERT_SUCCESS);
  }

  return(
    <div id="auth-settings-provider-container">
      {notificationMessage &&
        <AlertMessages
          id="auth-settings-details-notifications"
          message={notificationMessage}
          variant={notificationStatus}
          onClose={() => {
            setNotificationMessage('');
            setNotificationStatus('');
          }}
        />
      }
      {errors.length > 0 &&
        <ErrorContainer errors={errors} />
      }
      <Grid container>
        <Grid md={10} item>
          <Typography variant="h1" className={classes.pageTitle}>
            {intl.getI18nMessage('label.auth.provider.configuration')}
          </Typography>
        </Grid>
        <Grid md={2} item>
          <div className={classes.deleteContainer}>
            <Actions
              {...props}
              uuid={uuid}
              notifyMessages={notifyMessages}
              disabled={get(authDetails, 'defaultConfig') }
            />
          </div>
        </Grid>
      </Grid>
      <Grid container spacing={3} className={classes.gridContainer}>
        <Grid md={12} item>
          <Typography variant="h4">
            {getI18nFormattedMessage('label.auth.identity.provider.configuration')}
          </Typography>
          <div>
            {!isOktaIdpFlag &&
              <FormDefaultTextField
                id={'auth-settings-pc-saml-acs-url'}
                name={getI18nFormattedMessage('label.authorize.assertion.acs.url')}
                value={acsUrl2}
                error={isAcsUrl}
                defaultText={acsUrl1}
                defaultText1={acsUrl3}
                defaultTextClass={classes.defaultTextClass}
                defaultTextClass1={classes.defaultTextClass1}
                handleChange={(value) => handleChange('acsUrl', value, 255)}
                copyText={getI18nFormattedMessage('label.copy')}
                isDisabled={false}
                textInputClass={classes.defaultInputClass}
                onCopyClick={onCopyClick}
              />
            }
            {!isOktaIdpFlag && <div>
              <FormHelperText className={classes.helpText} error={isAcsUrl}>
                {intl.getI18nMessage('label.authorize.assertion.acs.url.help.text')}
              </FormHelperText>
            </div>
            }
          </div>
          <FormTextField
            id={'auth-settings-pc-saml-provider-url'}
            name={getI18nFormattedMessage('label.authorize.identity.provider.url')}
            value={providerUrl}
            error={isProviderUrl}
            handleChange={(value) => handleChange('providerUrl', value)}
            helperText={getI18nFormattedMessage('label.authorize.identity.provider.url.help.text')}
          />

          <FormTextField
            id={'auth-settings-pc-saml-issue-id'}
            name={getI18nFormattedMessage('label.authorize.identity.issue.id')}
            value={issueId}
            error={isIssueId}
            handleChange={(value) => handleChange('issueId', value)}
            helperText={getI18nFormattedMessage('label.authorize.identity.issue.id.help.text')}
          />
          <FileDropzone
            handleFiles={handleFiles}
            selectedFiles={sslFile}
            labelClass={classes.fileLabel}
            title={intl.getI18nMessage('label.auth.upload.trust.certificate')}
            id={'auth-settings-pc-saml-certficate-upload'}
            allowedFileExtentions={ALLOWED_FILE_EXTENSIONS}
            onRemoveFile={onRemoveFile}
            isValidation={isFileValidation}
            validationMessage={fileValidationMessage}
          />

          <Typography variant="h4">
            {getI18nFormattedMessage('label.auth.service.provider.configuration')}
          </Typography>

         <FormSelect
            id={'auth-settings-pc-saml-binding'}
            name={getI18nFormattedMessage('label.authorize.saml.binding')}
            value={samlBinding}
            error={isSamlBinding}
            data={SAML_BINDINGS}
            handleChange={(value) => handleChange('samlBinding', value)}
            helperText={intl.getI18nMessage('label.authorize.saml.binding.help.text')}
            noNativeSelect
            noNoneOption={true}
          />
          <FormTextField
            id={'auth-settings-pc-saml-token-attribute'}
            name={getI18nFormattedMessage('label.authorize.saml.token.attribute')}
            value={tokenAttribute}
            error={isTokenAttribute}
            handleChange={(value) => handleChange('tokenAttribute', value)}
            helperText={intl.getI18nMessage('label.authorize.saml.token.attribute.help.text')}
            disabled={true}
          />
          <FormSelect
            id={'auth-settings-pc-saml-token-attribute-in'}
            name={getI18nFormattedMessage('label.authorize.saml.token.attribute.in')}
            value={tokenAttributeIn}
            error={isTokenAttributeIn}
            data={SAML_TOKEN_ATTRIBUTES}
            handleChange={(value) => handleChange('tokenAttributeIn', value)}
            helperText={intl.getI18nMessage('label.authorize.saml.token.attribute.in.help.text')}
            noNativeSelect
            noNoneOption={true}
          />
          <FormTextField
            id={'auth-settings-pc-saml-service-provider'}
            name={intl.getI18nMessage('label.authorize.saml.service.provider.id')}
            value={providerId}
            handleChange={(value) => handleChange('providerId', value)}
            helperText={intl.getI18nMessage('label.authorize.saml.service.provider.id.help.text')}
            error={isProviderId}
          />
          {isOktaIdpFlag &&
            <>
              <Typography variant="h4">
                {getI18nFormattedMessage('label.auth.email.domains')}
              </Typography>
              <FormTextField
                id={'auth-settings-pc-saml-email-domains'}
                name={intl.getI18nMessage('label.authorize.saml.email.domains')}
                value={emailDomain}
                error={isEmailDomain}
                handleChange={(value) => handleChange('emailDomain', value)}
                helperText={intl.getI18nMessage('label.authorize.saml.email.domains.help.text')}
                disabled={uuid ? true : false}
              />
            </>
          }
        </Grid>
      </Grid>
      <FormActionButtons
        onNextClick={onNext}
        nextText={getI18nFormattedMessage('label.next.button')}
        onCancelClick={onCancel}
        showAdditional={true}
        onAdditionalClick={onSave}
        isDisabledAdditional={isSaveDisabled}
        id="auth-settings-provider-config-buttons"
      />
    </div>
  );
}

SamlProviderConfiguration.propTypes = {
  classes: shape({}),
  errors: shape({}),
  assets: arrayOf(shape({})),
  authDetails:shape({}),
  location: shape({}),
  config: shape({}),
  uuid: string,
  notifyMessages: func,
  setStateTabs: func,
  setAuthSettings: func,
  tabValue: string,
  saveAuthScheme: func,
  updateAuthScheme: func,
  onCancelDetails: func,
  push: func,
  hasAttributeMappingFields: func,
  hasDetailsFields: func,
  isOktaIdpFlag: bool,
};

const mapStateToProps = (state) => ({
  config: getConfig(state),
});

const mapDispatchToProps = {
  setAuthSettings,
  saveAuthScheme,
  updateAuthScheme,
};

export default compose(
withStyles(styles),
connect(mapStateToProps, mapDispatchToProps),
)(SamlProviderConfiguration);
